/**
 * Export Tab Component
 *
 * Component for exporting reports in different formats
 */

import React, {useState} from 'react';
import {Marketer} from '../models/report.model';
import {useReports} from '../hooks/useReports';
import {ExportRequest} from '../models/report.model';
import Button from '../../../Presentation/components/ui/Button';
import {AppCardTitle, AppText, AppLabelText, AppBodyText} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

interface ExportTabProps {
  marketer: Marketer;
}

export const ExportTab: React.FC<ExportTabProps> = ({marketer}) => {
  const {exportMarketerReport, isExporting, error} = useReports();
  const [selectedFormat, setSelectedFormat] = useState<'excel' | 'pdf' | 'json'>('excel');

  const handleExport = async () => {
    try {
      const request: ExportRequest = {
        format: selectedFormat
      };

      const result = await exportMarketerReport(marketer.id, request);

      if (selectedFormat === 'json') {
        // For JSON, download the data directly
        const dataStr = JSON.stringify(result, null, 2);
        const dataBlob = new Blob([dataStr], {type: 'application/json'});
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `marketer-${marketer.id}-report.json`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        // For Excel/PDF, redirect to download URL
        if ('download_url' in result) {
          window.open(result.download_url, '_blank');
        }
      }
    } catch (error) {
      StandardizedErrorHandler.getInstance().handleError(error, 'ExportTab.handleExport');
    }
  };

  const formats = [
    {value: 'excel', label: 'Excel (.xlsx)', icon: '📊'},
    {value: 'pdf', label: 'PDF', icon: '📄'},
    {value: 'json', label: 'JSON', icon: '🔧'}
  ] as const;

  // Error is handled by StandardizedErrorHandler and shown as toast notification
  // No need to display inline error

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <AppCardTitle className="text-gray-900 mb-4">تصدير التقرير</AppCardTitle>

        <div className="space-y-4">
          <div>
            <AppLabelText className="block text-gray-700 mb-2">
              اختر صيغة التصدير
            </AppLabelText>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {formats.map((format) => (
                <label
                  key={format.value}
                  className={`relative flex cursor-pointer rounded-lg p-4 focus:outline-none ${
                    selectedFormat === format.value
                      ? 'ring-2 ring-primary bg-brand-50'
                      : 'ring-1 ring-gray-300 bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name="format"
                    value={format.value}
                    checked={selectedFormat === format.value}
                    onChange={(e) => setSelectedFormat(e.target.value as any)}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <span className="text-2xl ml-3">{format.icon}</span>
                    <AppBodyText className="font-medium text-gray-900">{format.label}</AppBodyText>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="pt-4">
            <Button
              onClick={handleExport}
              disabled={isExporting}
              isLoading={isExporting}
              size="large"
              className="w-full md:w-auto"
            >
              {isExporting ? 'جاري التصدير...' : 'تصدير التقرير'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
